import { HiOutlineShoppingBag } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { FC, useState } from "react";
import { useCartStore } from "../store/cartStore";
import { toast } from "react-toastify";

type Props = {
  data: any;
};

const Cards: FC<Props> = ({ data }) => {
  const navigate = useNavigate();
  const { addItem } = useCartStore();
  const [addingToCart, setAddingToCart] = useState(false);


  // Get the image to display - prioritize featuredImage
  const displayImage = data.featuredImage || (data.images && data.images.length > 0 ? data.images[0] : null);

  // Get the price to display - prioritize salesPrice if available
  // const displayPrice = data.salesPrice !== "N/A" ? Number(data.salesPrice) : Number(data.price);
  const displayPrice = Number(data.price);



  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to product detail


    try {
      setAddingToCart(true);

      // Add item to cart (uses the API now)
      await addItem({
        id: data.id,
        name: data.name,
        image: displayImage || "",
        price: displayPrice,
        quantity: 1,
        type: 'product'
      });

      // Show success notification
      toast.success(`${data.name.slice(0, 20)}${data.name.length > 20 ? '...' : ''} added to cart!`);
    } catch (error: any) {
      // Handle authentication error
      if (error.message === "Authentication required") {
        // Toast is already shown by the store, just redirect after a delay
        setTimeout(() => {
          navigate("/auth/login");
        }, 800); // Reduced delay since auth check is now immediate
        return;
      }

      // Show error notification
      toast.error(error?.response?.data?.message || "Failed to add to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/products/${data.id}`)}
      className="w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-[415px]  border border-gray-200 rounded shadow-sm cursor-pointer hover:shadow-md transition-all duration-300"
    >
      {/* Image Container with Black Background */}
      <div className="w-full h-[180px] sm:h-[200px] md:h-[220px] lg:h-[280px] relative bg-black overflow-hidden">
        <img
          src={displayImage}
          className="h-full w-full object-cover"
          alt={data.name}
        />

        {data.onSale && data.discount > 0 && (
          <div className="absolute top-2 font-sfPro left-2 rounded-full  text-white px-2 py-1 text-xs font-medium z-10">
            {data.discount}% Off
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-3">
        <h5 className="text-sm font-medium font-sfPro line-clamp-2 h-10" title={data.name}>
          {data.name}
        </h5>

        {/* Stock Status Badge */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${data.stock > 0
              ? "bg-green-50 text-green-600 border border-green-200"
              : "bg-red-50 text-red-600 border border-red-200"
            }`}>
            {data.stock > 0 ? "In Stock" : "Out of Stock"}
          </span>
        </div>

        {/* Price & Shopping Icon */}
        <div className="flex justify-between items-center">
          <span className="text-black font-bold font-sfPro">
            ${displayPrice.toFixed(2)}
          </span>
          <button
            className={`h-8 w-8 transition-colors rounded-full flex justify-center items-center ${data.stock > 0
                ? "bg-black text-white hover:bg-gray-800"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            onClick={(e) => data.stock > 0 && handleAddToCart(e)}
            disabled={addingToCart || data.stock <= 0}
            aria-label={data.stock > 0 ? "Add to cart" : "Out of stock"}
          >
            {addingToCart ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            ) : (
              <HiOutlineShoppingBag className="text-sm" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export { Cards };
